import React, { forwardRef, useEffect, useState } from 'react';

import { Form } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  CustomIcon,
  CustomTooltip,
  EIconName,
  NumberInput,
  SwitchElement
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './free-minutes-item.scss';

// Models
import { IFreeMinutes } from '../../../../../../interfaces/counsellors.interfaces';

type TFormValue = {
  minutes: number;
  prepaid_enabled: boolean;
};

export type TFreeMinutesItemRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TFreeMinutesItemProps = {
  free_minutes: IFreeMinutes;
  toggleFreeMode: (checked: boolean, event: any) => void;
  saveChanges: (minutes: number, prepaid_enabled: boolean) => Promise<{ payload: any }>;
};

export const FreeMinutesItem = forwardRef<TFreeMinutesItemRef, TFreeMinutesItemProps>((
  {
    free_minutes,
    toggleFreeMode,
    saveChanges,
  }: TFreeMinutesItemProps, ref
) => {
  const [form] = Form.useForm<TFormValue>();
  const {
    free_service_minutes,
    free_buffer_minutes,
    free_minutes_enabled,
    prepaid_enabled,
    full_free_service_enabled
  } = free_minutes;

  const [switchChecked, setSwitchChecked] = useState<boolean>(free_minutes_enabled);
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  const [savingChanges, setSavingChanges] = useState<boolean>(false);

  const onValuesChange = (_: { [key in keyof TFormValue]: string }, { minutes, prepaid_enabled }: TFormValue): void => {
    const isMinutesChanged = minutes !== free_service_minutes;
    const isPrepaidEnabledChanged = prepaid_enabled !== free_minutes.prepaid_enabled;

    setHasChanges(isMinutesChanged || isPrepaidEnabledChanged);
  }

  const toggleFreeModeHandler = (checked: boolean, event: any): void => {
    toggleFreeMode(checked, event);

    if (!checked) {
      setHasChanges(false);
      form.resetFields();
    }
  }

  const saveChangesHandler = async (): Promise<void> => {
    const { minutes, prepaid_enabled } = form.getFieldsValue();
    setSavingChanges(true);
    const { payload } = await saveChanges(minutes, prepaid_enabled);
    setSavingChanges(false);

    if (!payload) return;
    setHasChanges(false);
  }

  useEffect(() => {
    setSwitchChecked(free_minutes_enabled);
  }, [free_minutes_enabled]);

  return (
    <Form form={ form } onValuesChange={ onValuesChange } className="free-minutes-item">
      <div className="free-minutes-item--item item--edit-price">
        <div className="edit-price-col edit-price-col--type">
          <div className={ `col-header` }/>
          <div className="col-body">
            <CustomIcon name={ EIconName.Av_Timer } size={ 'size-xll' }/>
            <span className="col-text">
            Free Minutes
          </span>
            <SwitchElement
              checked={ switchChecked } disabled={ full_free_service_enabled }
              onChange={ (checked, event) => toggleFreeModeHandler(checked, event) }
            />
          </div>
        </div>

        <div className="edit-price-col">
          <div className={ `col-header` }>
            Free minutes <CustomTooltip
            title={ 'Free minutes' }><CustomIcon
            name={ EIconName.Info }
            size={ 'size-md' }/></CustomTooltip>
          </div>
          <div className="col-body">
            <Form.Item name="minutes" initialValue={ free_service_minutes || 3 }>
              <NumberInput disabled={ !free_minutes_enabled || full_free_service_enabled }
                           min={ 3 }
                           max={ 999 }
                           placeholder={ 'Free minutes' }
                           parser={ (displayValue) => displayValue ? Math.round(+displayValue) : 0 }
                           controls={ false }/>
            </Form.Item>
            min
          </div>
        </div>

        <div className="edit-price-col">
          <div className={ `col-header` }>
            Prepay <CustomTooltip
            title={ 'Payment System will pre-authorize funds before the start of the call' }><CustomIcon
            name={ EIconName.Info }
            size={ 'size-md' }/></CustomTooltip>
          </div>
          <div className="col-body">
            <Form.Item name="prepaid_enabled" initialValue={ prepaid_enabled } valuePropName="checked">
              <SwitchElement disabled={ !free_minutes_enabled }/>
            </Form.Item>
          </div>
        </div>

        <div className="edit-price-col">
          <div className={ `col-header` }>
            Show Checkout notification minutes <CustomTooltip
            title={ 'Show Checkout notification minutes' }><CustomIcon
            name={ EIconName.Info }
            size={ 'size-md' }/></CustomTooltip>
          </div>
          <div className="col-body">
            <Form.Item>
              <NumberInput disabled={ true } defaultValue={ 1 }
                           placeholder={ 'Free minutes' }
                           parser={ (displayValue) => displayValue ? Math.round(+displayValue) : 0 }
                           controls={ false }/>
            </Form.Item>
            min
          </div>
        </div>

        <div className="edit-price-col">
          <div className={ `col-header` }>
            Buffer Time for Checkout <CustomTooltip
            title={ 'Buffer Time for Checkout' }><CustomIcon
            name={ EIconName.Info }
            size={ 'size-md' }/></CustomTooltip>
          </div>
          <div className="col-body">
            <Form.Item>
              <NumberInput disabled={ true } defaultValue={ free_buffer_minutes || 3 }
                           placeholder={ 'Free minutes' }
                           parser={ (displayValue) => displayValue ? Math.round(+displayValue) : 0 }
                           controls={ false }/>
            </Form.Item>
            min
          </div>
        </div>

        <div className="edit-price-col edit-price-col--action">
          <div className={ `col-header` }/>
          <div className="col-body" hidden={ !hasChanges || full_free_service_enabled }>
            <CustomButton disabled={ savingChanges || full_free_service_enabled }
                          size={ 'small' }
                          text={ 'Save' }
                          onClick={ saveChangesHandler }
            />
          </div>
        </div>
      </div>
    </Form>
  );
})
